import React, { useEffect, useRef, useState } from 'react';
import logo from '../images/icon_small.png';
import '../style/Groups.css';
import {
  IoAddCircle, IoPersonAdd, IoLockClosed, IoLockOpen, IoMoon, IoHandLeft,
  IoExitOutline, IoCloseSharp, IoCheckmarkSharp, IoArrowBack, IoPeople, IoLogInOutline, IoCamera, IoDocumentText,
  IoVideocam, IoCopyOutline, IoStop, IoTrashOutline
} from 'react-icons/io5';
import { FaCrown } from 'react-icons/fa';

import { imageSrc } from '../utils/avatar';
import { createGroup, listMyGroups, inviteMember, leaveGroup, deleteGroup, listMembers, removeMember, listIncomingInvites, acceptInvite, declineInvite, changeGroupImage, deleteGroupImage } from '../api/GroupAPI';
import { getFriends } from '../api/FriendsAPI';
import { useSocket } from '../socket/SocketContext';
import Modal from '../components/Modal';
import TranscriptHistoryModal from '../components/TranscriptHistoryModal';
import ImageCropModal, { mediaErrorText } from '../components/ImageCropModal';
import Avatar from '../components/Avatar';
import { MAX_IMAGE_BYTES, ACCEPTED_IMAGE_TYPES } from '../api/MediaAPI';
import { describeError } from '../utils/errorMessages';
import { getMyUserId } from '../utils/jwt';
import { useLanguage } from '../i18n/LanguageContext';
import { useConfirm } from '../hooks/useConfirm';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useHistory, useLocation } from 'react-router-dom';
import { createMeeting, listMyMeetings, endMeeting, meetingLink } from '../api/MeetingAPI';

const avatarUrl = (imageUrl) => imageSrc(imageUrl);
// 'group.png' is the schema's "no icon uploaded" sentinel and is deliberately
// not a real file on the server - fall back to the bundled logo instead.
const hasGroupIcon = (imageUrl) => !!imageUrl && imageUrl !== 'group.png';
const groupIconUrl = (imageUrl) => (hasGroupIcon(imageUrl) ? avatarUrl(imageUrl) : logo);
// Only the bundled-logo fallback gets the theme tint - a real uploaded icon
// is a picture, and hue-rotating a picture makes it look like a negative.
const groupIconClass = (imageUrl) => (hasGroupIcon(imageUrl) ? '' : ' BrandFallbackImg');
const username = () => localStorage.getItem('username');
const token = () => localStorage.getItem('accessToken');

const DOOR_STATES = ['knock', 'open', 'dnd', 'locked'];
const DOOR_ICON = { knock: <IoHandLeft />, open: <IoLockOpen />, dnd: <IoMoon />, locked: <IoLockClosed /> };

function RoomCard({ room, isMine, isCurrentRoom, isGroupOwner, groupOwnerId, highlight, onEnter, onKnock, onSetDoorState, onKick, onHistory, t }) {
  // Phones have no hover, so an occupant avatar's `title` never shows - the
  // name is written out next to it there instead.
  const { isMobile } = useLayoutMode();
  const isPersonal = room.type === 'personal';
  const blocked = isPersonal && !isMine && (room.availability === 'offline' || room.availability === 'ownerless' || room.doorState === 'locked' || room.doorState === 'dnd');
  // This room's owner is the person who owns the whole *group* (not just
  // this personal room) - every member has their own personal room, but
  // only one of them founded the group.
  const isGroupOwnerRoom = isPersonal && room.owner && groupOwnerId && room.owner.userId === String(groupOwnerId);

  // Already physically in this exact room (e.g. a guest who knocked their
  // way into someone else's room, which may since have gone back to a
  // non-open doorState) - knocking on your own current room makes no sense,
  // so this always counts as "entering" even when the knock branch below
  // otherwise would have fired.
  const primaryAction = () => {
    if (isMine || isCurrentRoom || !isPersonal || room.doorState === 'open') onEnter(room);
    else onKnock(room);
  };

  // Arrived from a friend's "Gruba git" (Friends page): bring this room
  // into view and flash it.
  const cardRef = useRef(null);
  useEffect(() => {
    if (highlight && cardRef.current && cardRef.current.scrollIntoView) cardRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlight]);

  return (
    <div ref={cardRef} className={`RoomCard glassCard${blocked ? ' RoomCardBlocked' : ''}${highlight ? ' RoomCardHighlight' : ''}`}>
      <div className="RoomCardHeader">
        <span className="RoomCardTitle">
          {isPersonal && room.owner && (
            <Avatar className="RoomOwnerAvatar" imageUrl={room.owner.imageUrl} alt={room.owner.username} username={room.owner.username} seed={room.owner} />
          )}
          <span className="RoomCardName">
            {isPersonal && room.owner ? `${room.owner.fullName || room.owner.username}${isMine ? t('groups.youSuffix') : ''}` : room.name}
          </span>
          {isGroupOwnerRoom && <FaCrown className="GroupOwnerCrown" title={t('groups.groupOwner')} />}
        </span>
        {isPersonal && <span className="RoomDoorIcon" title={t(`groups.doorState.${room.doorState}`)}>{DOOR_ICON[room.doorState]}</span>}
      </div>
      {isPersonal && <div className="descGray RoomAvailability">{t(`groups.availability.${room.availability}`)}</div>}
      <div className="RoomOccupants">
        {room.occupants.map((o) => (
          <span key={o.userId} className="RoomOccupantChip" title={o.username}>
            <Avatar imageUrl={o.imageUrl} alt={o.username} username={o.username} seed={o} />
            {isMobile && <span className="RoomOccupantName">{o.fullName || o.username}</span>}
            {isGroupOwner && room.type === 'public' && (
              <button className="RoomKickBtn" title={t('groups.kick')} onClick={() => onKick(room, o)}><IoCloseSharp /></button>
            )}
          </span>
        ))}
        {room.occupants.length === 0 && <span className="descGray">{t('groups.roomEmpty')}</span>}
      </div>
      <div className="RoomCardActions">
        {isMine && (
          <select value={room.doorState} onChange={(e) => onSetDoorState(room, e.target.value)} className="RoomDoorSelect">
            {DOOR_STATES.map((d) => <option key={d} value={d}>{t(`groups.doorState.${d}`)}</option>)}
          </select>
        )}
        {/* Independent of blocked/entering - past transcript sessions are
            readable regardless of whether this room can currently be
            entered (the backend enforces "were you actually a participant",
            see transcriptController.js, not "can you enter now"). */}
        <button onClick={() => onHistory(room)} className="RoomHistoryBtn secondaryColor" title={t('transcript.historyTitle')}>
          <IoDocumentText />
        </button>
        {!blocked && (() => {
          const entering = isMine || isCurrentRoom || !isPersonal || room.doorState === 'open';
          return (
            <button onClick={primaryAction} className={`RoomEnterBtn ${entering ? 'primaryColor' : 'secondaryColor'}`}>
              {entering ? <IoLogInOutline /> : <IoHandLeft />}
              <span>{entering ? t('groups.enter') : t('groups.knock')}</span>
            </button>
          );
        })()}
      </div>
    </div>
  );
}

export default function Groups() {
  const { groupState, connected, syncGroup, joinRoom, sendKnock, setDoorState, kickFromRoom, pushToast, unreadGroupInvites, clearGroupInviteNotifications } = useSocket();
  const { t } = useLanguage();
  const [confirm, confirmEl] = useConfirm();
  const history = useHistory();
  const location = useLocation();

  const groupIconInputRef = useRef(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [highlightRoomId, setHighlightRoomId] = useState(null);

  // The Friends page's "Gruba git" opens a group at a friend's room
  // (location.state.focusGroup = { groupId, roomId }). The state is cleared
  // right away so a reload doesn't jump there again.
  useEffect(() => {
    const focus = location.state && location.state.focusGroup;
    if (!focus) return undefined;
    setSelectedGroupId(focus.groupId);
    setHighlightRoomId(focus.roomId || null);
    history.replace(location.pathname);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // The flash is brief - its own effect, since the replace() above changes
  // location.key and would cancel a timer set there.
  useEffect(() => {
    if (!highlightRoomId) return undefined;
    const timer = setTimeout(() => setHighlightRoomId(null), 2600);
    return () => clearTimeout(timer);
  }, [highlightRoomId]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const [showInvite, setShowInvite] = useState(false);
  const [friends, setFriends] = useState([]);
  const [existingMembers, setExistingMembers] = useState([]); // members + already-invited (not invite-able again)

  const [showMembers, setShowMembers] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);

  const [incomingInvites, setIncomingInvites] = useState([]);

  const [showTranscriptHistory, setShowTranscriptHistory] = useState(false);
  const [transcriptHistoryRoomId, setTranscriptHistoryRoomId] = useState(null);

  // Meetings I am hosting right now. Kept here rather than per-group because
  // a meeting is not owned by a group (see Backend/models/meeting.js) - the
  // groupId is only a note about where it was started from, so the same
  // meeting is still mine no matter which group I'm looking at.
  const [myMeetings, setMyMeetings] = useState([]);
  const [creatingMeeting, setCreatingMeeting] = useState(false);

  const loadGroups = async () => {
    const res = await listMyGroups(username(), token());
    if (res.status === 'success') setGroups(res.data);
  };

  const loadIncomingInvites = async () => {
    const res = await listIncomingInvites(username(), token());
    if (res.status === 'success') setIncomingInvites(res.data);
  };

  const loadMyMeetings = async () => {
    const res = await listMyMeetings(token());
    if (res.status === 'success') setMyMeetings(res.data.meetings);
  };

  useEffect(() => {
    (async () => { await Promise.all([loadGroups(), loadIncomingInvites(), loadMyMeetings()]); setLoading(false); clearGroupInviteNotifications(); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (unreadGroupInvites > 0) {
      loadIncomingInvites();
      clearGroupInviteNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadGroupInvites]);

  // Also re-fires on `connected` flipping true - a group selected (or the
  // app opened straight into one) before the socket actually finished
  // connecting would otherwise send presence:sync on a not-yet-connected
  // socket and never retry, leaving the room list empty until something
  // else (a manual refresh) happened to trigger a resync.
  useEffect(() => {
    if (!selectedGroupId || !connected) return;
    syncGroup(selectedGroupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, connected]);

  // The group owner removed us (see group:youWereRemoved in SocketContext) -
  // drop it from our own group list and back out of it if we were looking at it.
  useEffect(() => {
    if (!groupState.lastRemovedGroupId) return;
    setGroups((prev) => prev.filter((g) => g.groupId !== groupState.lastRemovedGroupId));
    setSelectedGroupId((prev) => (prev === groupState.lastRemovedGroupId ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupState.lastRemovedGroupId]);

  // Picked an icon: size-check, then crop (same flow as the avatar in
  // Settings) - the upload happens on the crop modal's confirm.
  const [iconCropFile, setIconCropFile] = useState(null);
  const pickGroupIcon = (file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      pushToast(mediaErrorText(t, { code: 'FILE_TOO_LARGE' }));
      return;
    }
    setIconCropFile(file);
  };

  const changeGroupIcon = async (crop) => {
    const file = iconCropFile;
    setIconCropFile(null);
    const res = await changeGroupImage(selectedGroupId, file, crop);
    if (res.status === 'success') {
      // Patch in place rather than refetching the whole list - the only
      // thing that changed is this one group's icon.
      setGroups((prev) => prev.map((g) => (g.groupId === selectedGroupId ? { ...g, imageUrl: res.imageUrl } : g)));
    } else {
      pushToast(res.code ? mediaErrorText(t, res) : describeError(res, t));
    }
  };

  const removeGroupIcon = async () => {
    const res = await deleteGroupImage(username(), token(), selectedGroupId);
    if (res.status === 'success') {
      setGroups((prev) => prev.map((g) => (g.groupId === selectedGroupId ? { ...g, imageUrl: res.imageUrl } : g)));
    } else {
      pushToast(describeError(res, t));
    }
  };

  const selectedGroup = groups.find((g) => g.groupId === selectedGroupId);
  const isGroupOwner = !!selectedGroup && selectedGroup.role === 'owner';
  const rooms = selectedGroupId ? (groupState.roomsByGroup[selectedGroupId] || []) : [];

  const submitCreate = async () => {
    const trimmed = newGroupName.trim();
    if (!trimmed) return;
    const res = await createGroup(username(), token(), trimmed);
    if (res.status === 'success') {
      setNewGroupName('');
      setShowCreate(false);
      await loadGroups();
      setSelectedGroupId(res.data.group._id);
    } else {
      pushToast(res.message);
    }
  };

  const openInvite = async () => {
    const [friendsRes, membersRes] = await Promise.all([
      getFriends(username(), token()),
      listMembers(username(), token(), selectedGroupId)
    ]);
    if (friendsRes.status === 'success') setFriends(friendsRes.data);
    if (membersRes.status === 'success') setExistingMembers(membersRes.data.map((m) => m.username));
    setShowInvite(true);
  };

  const invite = async (invitedUsername) => {
    const res = await inviteMember(username(), token(), selectedGroupId, invitedUsername);
    if (res.status === 'success') {
      // Not a member yet - just invited. Still drop them from the
      // invite-able list so the button can't be clicked twice.
      setExistingMembers((prev) => [...prev, invitedUsername]);
      pushToast(t('groups.inviteSent'));
    } else {
      pushToast(describeError(res, t));
    }
  };

  const acceptGroupInvite = async (invite) => {
    const res = await acceptInvite(username(), token(), invite.inviteId);
    if (res.status === 'success') {
      setIncomingInvites((prev) => prev.filter((i) => i.inviteId !== invite.inviteId));
      await loadGroups();
      setSelectedGroupId(invite.groupId);
    } else {
      pushToast(describeError(res, t));
    }
  };

  const declineGroupInvite = async (invite) => {
    const res = await declineInvite(username(), token(), invite.inviteId);
    if (res.status === 'success') {
      setIncomingInvites((prev) => prev.filter((i) => i.inviteId !== invite.inviteId));
    } else {
      pushToast(describeError(res, t));
    }
  };

  const openMembers = async () => {
    const res = await listMembers(username(), token(), selectedGroupId);
    if (res.status === 'success') setGroupMembers(res.data);
    setShowMembers(true);
  };

  const askRemoveMember = (member) => confirm({
    message: t('groups.removeMemberConfirm', { name: member.fullName || member.username }),
    confirmLabel: t('groups.removeMember'),
    onConfirm: () => removeMemberFromGroup(member.username)
  });

  const removeMemberFromGroup = async (targetUsername) => {
    const res = await removeMember(username(), token(), selectedGroupId, targetUsername);
    if (res.status === 'success') {
      setGroupMembers((prev) => prev.filter((m) => m.username !== targetUsername));
      await loadGroups();
    } else {
      pushToast(describeError(res, t));
    }
  };

  const askLeaveGroup = () => confirm({
    message: t('groups.leaveGroupConfirm', { name: selectedGroup ? selectedGroup.name : '' }),
    confirmLabel: t('groups.leaveGroup'),
    onConfirm: leaveCurrentGroup
  });

  const leaveCurrentGroup = async () => {
    const res = await leaveGroup(username(), token(), selectedGroupId);
    if (res.status === 'success') {
      setSelectedGroupId(null);
      loadGroups();
    } else {
      pushToast(res.message);
    }
  };

  const askDeleteGroup = () => confirm({
    message: t('groups.deleteGroupConfirm', { name: selectedGroup ? selectedGroup.name : '' }),
    confirmLabel: t('groups.deleteGroup'),
    onConfirm: deleteCurrentGroup
  });

  const deleteCurrentGroup = async () => {
    const res = await deleteGroup(username(), token(), selectedGroupId);
    if (res.status === 'success') {
      pushToast(t('groups.groupDeleted'));
      setSelectedGroupId(null);
      loadGroups();
    } else {
      pushToast(describeError(res, t));
    }
  };

  const handleEnter = async (room) => {
    const res = await joinRoom(room.roomId);
    if (!res.ok) pushToast(describeError(res, t));
  };

  const handleKnock = async (room) => {
    const res = await sendKnock(room.roomId);
    if (!res.ok) pushToast(describeError(res, t));
  };

  const handleSetDoorState = async (room, doorState) => {
    const res = await setDoorState(room.roomId, doorState);
    if (!res.ok) pushToast(describeError(res, t));
  };

  const handleKick = (room, occupant) => confirm({
    message: t('groups.kickConfirm', { name: occupant.fullName || occupant.username }),
    confirmLabel: t('groups.kick'),
    onConfirm: async () => {
      const res = await kickFromRoom(room.roomId, occupant.userId);
      if (!res.ok) pushToast(describeError(res, t));
    }
  });

  // Starts a meeting "from" this group: the link is copied right away,
  // because sending it to someone is the first thing anyone does, and only
  // then do we open the meeting itself. Copying can fail on an insecure
  // origin or without a fresh user gesture - the link is also shown inside
  // the meeting's own info popover, so that isn't worth blocking the flow for.
  const startMeeting = async () => {
    setCreatingMeeting(true);
    const res = await createMeeting(token(), {
      title: selectedGroup ? selectedGroup.name : undefined,
      groupId: selectedGroupId || undefined
    });
    setCreatingMeeting(false);
    if (res.status !== 'success') {
      pushToast(res.message || t('meeting.createFailed'));
      return;
    }
    const { code } = res.data.meeting;
    try {
      await navigator.clipboard.writeText(meetingLink(code));
      pushToast(t('meeting.linkCopied'));
    } catch (error) { /* the link is shown again inside the meeting */ }
    history.push(`/meet/${code}`);
  };

  const copyMeetingLink = async (code) => {
    try {
      await navigator.clipboard.writeText(meetingLink(code));
      pushToast(t('meeting.linkCopied'));
    } catch (error) {
      pushToast(meetingLink(code));
    }
  };

  const askEndMeeting = (meeting) => confirm({
    message: t('meeting.endConfirm', { title: meeting.title }),
    confirmLabel: t('meeting.endForAll'),
    onConfirm: async () => {
      const res = await endMeeting(token(), meeting.code);
      if (res.status === 'success') setMyMeetings((prev) => prev.filter((m) => m.code !== meeting.code));
      else pushToast(res.message || t('meeting.errors.endFailed'));
    }
  });

  const openTranscriptHistory = (room) => {
    setTranscriptHistoryRoomId(room.roomId);
    setShowTranscriptHistory(true);
  };

  return (
    <>
      <Modal mState={{ iShow: showCreate, setiShow: setShowCreate }} buttonShow>
        <h4 style={{ marginTop: 0 }}>{t('groups.createModalTitle')}</h4>
        <input
          className="GroupNameInput"
          placeholder={t('groups.groupNamePlaceholder')}
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); }}
          autoFocus
        />
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 8 }}>
          <button onClick={submitCreate} className="SendButton primaryColor">{t('groups.create')}</button>
          <button onClick={() => setShowCreate(false)} className="SendButton secondaryColor">{t('common.cancel')}</button>
        </div>
      </Modal>

      <Modal mState={{ iShow: showInvite, setiShow: setShowInvite }} buttonShow>
        <h4 style={{ marginTop: 0 }}>{t('groups.inviteModalTitle')}</h4>
        <ul className="FriendList">
          {friends.filter((f) => !existingMembers.includes(f.username)).map((f) => (
            <li key={f.username} className="glassCard">
              <Avatar imageUrl={f.imageUrl} alt={f.username} username={f.username} seed={f} />
              <div>
                {f.fullName || f.username}
                <aside className="descGray">@{f.username}</aside>
              </div>
              <button onClick={() => invite(f.username)} className="AddButton primaryColor" title={t('groups.invite')}><IoPersonAdd /></button>
            </li>
          ))}
          {friends.length > 0 && friends.every((f) => existingMembers.includes(f.username)) &&
            <p className="descGray">{t('groups.allFriendsInGroup')}</p>}
          {friends.length === 0 && <p className="descGray">{t('groups.addFriendsFirst')}</p>}
        </ul>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <button onClick={() => setShowInvite(false)} className="SendButton secondaryColor">{t('common.close')}</button>
        </div>
      </Modal>

      <Modal mState={{ iShow: showMembers, setiShow: setShowMembers }} buttonShow>
        <h4 style={{ marginTop: 0 }}>{t('groups.membersModalTitle')}</h4>
        <ul className="FriendList">
          {groupMembers.filter((m) => m.role !== 'owner').map((m) => (
            <li key={m.username} className="glassCard">
              <Avatar imageUrl={m.imageUrl} alt={m.username} username={m.username} seed={m} />
              <div>
                {m.fullName || m.username}
                <aside className="descGray">@{m.username}</aside>
              </div>
              <button onClick={() => askRemoveMember(m)} className="AddButton cancelColor" title={t('groups.removeMember')}><IoCloseSharp /></button>
            </li>
          ))}
          {groupMembers.filter((m) => m.role !== 'owner').length === 0 && <p className="descGray">{t('groups.noOtherMembers')}</p>}
        </ul>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <button onClick={() => setShowMembers(false)} className="SendButton secondaryColor">{t('common.close')}</button>
          <button onClick={() => { setShowMembers(false); askDeleteGroup(); }} className="SendButton cancelColor GroupDeleteButton">
            <IoTrashOutline /> {t('groups.deleteGroup')}
          </button>
        </div>
      </Modal>

      <TranscriptHistoryModal
        mState={{ iShow: showTranscriptHistory, setiShow: setShowTranscriptHistory }}
        roomId={transcriptHistoryRoomId}
      />

      <div className={selectedGroup ? "SplitPage HasSelection" : "SplitPage"}>
      <div className="ChannelContainer glass">
        <h4>{t('groups.title')}</h4>
        <p className="descGray">{t('groups.subtitle')}</p>
        <button onClick={() => setShowCreate(true)} className="AddButton primaryColor" style={{ marginTop: 8 }} title={t('groups.createGroupTitle')}>
          <IoAddCircle /> {t('groups.newGroup')}
        </button>
        {incomingInvites.length > 0 && (
          <>
            <h4 style={{ padding: '16px 20px 0' }}>{t('groups.incomingInvitesTitle')}</h4>
            <ul className="FriendList GroupInviteList">
              {incomingInvites.map((inv) => (
                <li key={inv.inviteId} className="glassCard">
                  <Avatar imageUrl={inv.inviter && inv.inviter.imageUrl} alt={inv.inviter ? inv.inviter.username : ''} username={inv.inviter && inv.inviter.username} seed={inv.inviter} />
                  <div>
                    {t('groups.invitedToGroup', { inviter: inv.inviter ? (inv.inviter.fullName || inv.inviter.username) : '?', group: inv.groupName })}
                  </div>
                  <button onClick={() => acceptGroupInvite(inv)} className="AddButton primaryColor" title={t('groups.acceptInvite')}><IoCheckmarkSharp /></button>
                  <button onClick={() => declineGroupInvite(inv)} className="AddButton cancelColor" title={t('groups.declineInvite')}><IoCloseSharp /></button>
                </li>
              ))}
            </ul>
          </>
        )}
        <ul className="Channels">
          {!loading && groups.length === 0 && <p className="descGray">{t('groups.noGroupsYet')}</p>}
          {groups.map((g) => (
            <li key={g.groupId} onClick={() => setSelectedGroupId(g.groupId)} className={selectedGroupId === g.groupId ? 'ChActive glassCard' : 'glassCard'}>
              <img className={`GroupListIcon${groupIconClass(g.imageUrl)}`} src={groupIconUrl(g.imageUrl)} alt="" />
              <div className="ChCon">
                <b>{g.name}</b>
                <aside className="descGray">{t('groups.memberCount', { count: g.memberCount })}{g.role === 'owner' ? t('groups.youOwnThis') : ''}</aside>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="ChatContainer">
        {selectedGroup ? (
          <>
            <div className="ChatHeader glass">
              <button className="MobileBackBtn NaviButton" onClick={() => setSelectedGroupId(null)} title={t('common.back')}><IoArrowBack /></button>
              {/* Owner-only upload target, same interaction as the profile
                  avatar in Settings: the image itself is the button. The
                  remove badge sits on its corner (like RoomKickBtn on an
                  occupant's avatar) instead of being a same-looking button
                  lost among invite/manage-members/leave in the action bar. */}
              {isGroupOwner ? (
                <div className="GroupHeaderIconWrap">
                  <button
                    type="button"
                    className="GroupHeaderIcon GroupHeaderIconEditable"
                    onClick={() => groupIconInputRef.current.click()}
                    title={t('groups.changeIcon')}
                  >
                    <img className={groupIconClass(selectedGroup.imageUrl).trim()} src={groupIconUrl(selectedGroup.imageUrl)} alt="" />
                    <span className="GroupHeaderIconOverlay"><IoCamera /></span>
                  </button>
                  {selectedGroup.imageUrl && selectedGroup.imageUrl !== 'group.png' && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeGroupIcon(); }}
                      className="GroupHeaderIconRemoveBtn"
                      title={t('groups.removeIcon')}
                    >
                      <IoCloseSharp />
                    </button>
                  )}
                </div>
              ) : (
                <span className="GroupHeaderIcon"><img className={groupIconClass(selectedGroup.imageUrl).trim()} src={groupIconUrl(selectedGroup.imageUrl)} alt="" /></span>
              )}
              <input
                type="file"
                ref={groupIconInputRef}
                className="GroupIconInput"
                accept={ACCEPTED_IMAGE_TYPES}
                onChange={(e) => { pickGroupIcon(e.target.files[0]); e.target.value = ''; }}
              />
              <div className="UserDetails">
                {selectedGroup.name}
                <aside className="descGray">{t('groups.memberCount', { count: selectedGroup.memberCount })}</aside>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button onClick={openInvite} className="AddButton primaryColor" title={t('groups.inviteFriend')}><IoPersonAdd /></button>
                {isGroupOwner && (
                  <button onClick={openMembers} className="AddButton secondaryColor" title={t('groups.manageMembers')}><IoPeople /></button>
                )}
                {!isGroupOwner && (
                  <button onClick={askLeaveGroup} className="AddButton cancelColor" title={t('groups.leaveGroup')}><IoExitOutline /></button>
                )}
              </div>
            </div>
            {/* Sits above the room grid, not inside it: a meeting is not a
                room (no door state, no membership, no presence) and putting it
                among the RoomCards would imply it behaves like one. */}
            <div className="MeetingStrip glassCard">
              <div className="MeetingStripMain">
                <span className="MeetingStripIcon"><IoVideocam /></span>
                <div className="MeetingStripText">
                  <b>{t('meeting.startMeeting')}</b>
                  <span className="descGray">{t('meeting.startMeetingDesc')}</span>
                </div>
                <button onClick={startMeeting} className="SendButton primaryColor" disabled={creatingMeeting}>
                  {creatingMeeting ? t('meeting.creating') : t('meeting.startMeeting')}
                </button>
              </div>
              {myMeetings.length > 0 && (
                <ul className="MeetingStripList">
                  {myMeetings.map((meeting) => (
                    <li key={meeting.code}>
                      <span className="MeetingStripTitle">{meeting.title}</span>
                      <code className="MeetingStripCode">{meeting.code}</code>
                      <button onClick={() => copyMeetingLink(meeting.code)} className="AddButton secondaryColor" title={t('meeting.copyLink')}><IoCopyOutline /></button>
                      <button onClick={() => history.push(`/meet/${meeting.code}`)} className="AddButton primaryColor" title={t('meeting.joinYours')}><IoLogInOutline /></button>
                      <button onClick={() => askEndMeeting(meeting)} className="AddButton cancelColor" title={t('meeting.endForAll')}><IoStop /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="RoomGrid">
              {rooms.map((room) => (
                <RoomCard
                  key={room.roomId}
                  room={room}
                  isMine={room.type === 'personal' && room.ownerId === getMyUserId()}
                  isCurrentRoom={room.roomId === groupState.currentRoomId}
                  isGroupOwner={isGroupOwner}
                  groupOwnerId={selectedGroup.ownerId}
                  highlight={room.roomId === highlightRoomId}
                  onEnter={handleEnter}
                  onKnock={handleKnock}
                  onSetDoorState={handleSetDoorState}
                  onKick={handleKick}
                  onHistory={openTranscriptHistory}
                  t={t}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="ChatHeader glass">
            <div className="UserDetails descGray">{t('groups.selectGroup')}</div>
          </div>
        )}
      </div>
      </div>
      <ImageCropModal
        file={iconCropFile}
        title={t('media.cropGroupIconTitle')}
        onCancel={() => setIconCropFile(null)}
        onConfirm={changeGroupIcon}
        onInvalid={() => { setIconCropFile(null); pushToast(mediaErrorText(t, { code: 'UNSUPPORTED_TYPE' })); }}
      />
      {/* Last, so it stacks above the members modal it's opened from. */}
      {confirmEl}
    </>
  );
}
